import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { getCachedPageVisibilityInRevision } from '@gitbook/hive-core';
import {
    createPalette,
    PaletteButton,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    FieldGroup,
    Group,
    Label,
    LabeledContainer,
    LabeledContainerBody,
    LabeledContainerHeader,
    Separator,
    Switch,
} from '@gitbook/spine-react';

import { withPageCommands } from '../withPageCommands';
import type { PageOptionsTabProps } from './types';

/**
 * Tab for managing the visibility of a page.
 */
export const PageOptionsTabVisibility = withPageCommands<PageOptionsTabProps>(
    function PageOptionsTabVisibilityImpl(props): React.ReactElement {
        const {
            analyticsContext,
            space,
            branch,
            revision,
            page,
            adaptiveContentContext,
            siteAudience,
        } = props;

        const visibility = React.useMemo(() => {
            return getCachedPageVisibilityInRevision(revision, page.id);
        }, [revision, page.id]);

        const palette = React.useMemo(() => {
            if (!siteAudience?.options) {
                return null;
            }

            const commands = siteAudience.options;
            return () =>
                createPalette({
                    position: {
                        width: PalettePositionWidth.ANCHOR,
                        maxHeight: PalettePositionMaxHeight.AUTO,
                    },
                    groups: [
                        {
                            key: 'siteAudience',
                            commands,
                        },
                    ],
                });
        }, [siteAudience?.options]);

        return (
            <>
                {siteAudience?.dialogs}
                <div className="flex flex-1 flex-col gap-6 p-6">
                    {adaptiveContentContext && palette ? (
                        <>
                            <LabeledContainer>
                                <LabeledContainerHeader>
                                    <Label>Site audience</Label>
                                    <Description>
                                        Choose who can access this page on your site
                                    </Description>
                                </LabeledContainerHeader>
                                <LabeledContainerBody>
                                    <PaletteButton
                                        palette={palette}
                                        kind="secondary"
                                        trailingIcon={Icon.ChevronDown}
                                        label={page.condition ? 'Conditional' : 'Default'}
                                    />
                                </LabeledContainerBody>
                            </LabeledContainer>
                            <Separator />
                        </>
                    ) : null}
                    <div className="flex flex-col gap-3">
                        <FieldGroup orientation="horizontal">
                            <Group>
                                <Label>Hide page</Label>
                                <Description>
                                    Hide this page from the navigation table of content. The page
                                    will still remain accessible via direct link.
                                </Description>
                            </Group>
                            <Switch
                                value={visibility.hidden.value}
                                disabled={
                                    visibility.hidden.setFromAncestor ||
                                    visibility.hidden.setFromOther
                                }
                                onValueChange={(value) => {
                                    branch.change((change) => {
                                        change.updatePageVisibility(page.id, {
                                            hidden: value,
                                        });
                                    });

                                    analytics.logEvent(
                                        analytics.createPageEvent(space, page, {
                                            ...analyticsContext,
                                            type: 'edit_toggle_hidden',
                                            hidden: value,
                                        })
                                    );
                                }}
                            />
                        </FieldGroup>

                        {visibility.hidden.setFromAncestor ? (
                            <DisabledMessage>Inherited from a parent page.</DisabledMessage>
                        ) : null}
                    </div>
                    <div className="flex flex-col gap-3">
                        <FieldGroup orientation="horizontal">
                            <Group>
                                <Label>Search indexing</Label>
                                <Description>
                                    Index this page in the content search engine and Ask AI.
                                </Description>
                            </Group>
                            <Switch
                                value={!visibility.noIndex.value}
                                disabled={
                                    visibility.noIndex.setFromAncestor ||
                                    visibility.noIndex.setFromOther
                                }
                                onValueChange={(value) => {
                                    branch.change((change) => {
                                        change.updatePageVisibility(page.id, {
                                            noIndex: value === false,
                                        });
                                    });

                                    analytics.logEvent(
                                        analytics.createPageEvent(space, page, {
                                            ...analyticsContext,
                                            type: 'page_edit_options',
                                            pageProperties: ['noIndex'],
                                        })
                                    );
                                }}
                            />
                        </FieldGroup>
                        {visibility.noIndex.setFromAncestor ? (
                            <DisabledMessage>Inherited from a parent page.</DisabledMessage>
                        ) : null}
                        {visibility.noIndex.value && !visibility.noIndex.setFromAncestor ? (
                            <DisabledMessage>
                                Any sub-pages of this page won't be indexed either.
                            </DisabledMessage>
                        ) : null}
                    </div>
                    <div className="flex flex-col gap-3">
                        <FieldGroup orientation="horizontal">
                            <Group>
                                <Label>Web-crawlers indexing</Label>
                                <Description>
                                    Allow search engines and web-crawlers (Google, ChatGPT, etc.) to
                                    index this page. It can be disabled globally in the site
                                    settings.
                                </Description>
                            </Group>
                            <Switch
                                value={!visibility.noRobotsIndex.value}
                                disabled={
                                    visibility.noRobotsIndex.setFromAncestor ||
                                    visibility.noRobotsIndex.setFromOther
                                }
                                onValueChange={(value) => {
                                    branch.change((change) => {
                                        change.updatePageVisibility(page.id, {
                                            noRobotsIndex: value === false,
                                        });
                                    });

                                    analytics.logEvent(
                                        analytics.createPageEvent(space, page, {
                                            ...analyticsContext,
                                            type: 'page_edit_options',
                                            pageProperties: ['noRobotsIndex'],
                                        })
                                    );
                                }}
                            />
                        </FieldGroup>

                        {visibility.noRobotsIndex.setFromOther ? (
                            <DisabledMessage>
                                Disabled because search indexing is turned off.
                            </DisabledMessage>
                        ) : visibility.noRobotsIndex.setFromAncestor ? (
                            <DisabledMessage>Inherited from a parent page.</DisabledMessage>
                        ) : null}
                    </div>
                </div>
            </>
        );
    }
);

function DisabledMessage(props: { children: React.ReactNode }) {
    return (
        <div className="flex gap-3 text-muted text-ui-small">
            <Icon.ArrowCurveDownRight className="size-4" />
            <div className="flex-1">{props.children}</div>
        </div>
    );
}
